import React from 'react'

const FALLBACK_IMG = '/assets/icons/category-character.png'

const LAYER_STYLES = {
  wrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  background: {
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    overflow: 'hidden',
    zIndex: 0,
  },
  character: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  hat: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    zIndex: 2,
    pointerEvents: 'none',
  },
  handItem: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    zIndex: 2,
    pointerEvents: 'none',
  },
}

const Sparkles = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 3, overflow: 'hidden' }}>
    {[
      { top: '10%', left: '8%', size: 8, delay: 0 },
      { top: '18%', right: '12%', size: 6, delay: 0.4 },
      { bottom: '22%', left: '15%', size: 5, delay: 0.8 },
      { top: '40%', right: '5%', size: 7, delay: 1.2 },
    ].map((s, i) => (
      <div
        key={i}
        style={{
          position: 'absolute',
          top: s.top, bottom: s.bottom, left: s.left, right: s.right,
          width: s.size, height: s.size,
          borderRadius: '50%',
          background: '#FFD700',
          boxShadow: '0 0 6px 2px rgba(255,215,0,.5)',
          animation: `sparkle 2s ${s.delay}s infinite`,
        }}
      />
    ))}
  </div>
)

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''

const AvatarDisplay = ({ avatar, userAvatar, size = 140, onClick, showSparkles = true, naked = false }) => {
  const charName = capitalize(avatar?.character || 'explorer')
  const characterSrc = avatar?.skin
    ? `/assets/avatar/clothes/${avatar.skin}/${avatar.skin}-${charName}.png`
    : avatar?.character
      ? `/assets/avatar/characters/${charName}.png`
      : null
  const hatSrc = avatar?.hat
    ? `/assets/avatar/hats/${avatar.hat}.png`
    : null
  const handItemSrc = avatar?.handItem
    ? `/assets/avatar/hand-items/${avatar.handItem}.png`
    : null
  const bgSrc = avatar?.background === null
    ? null
    : avatar?.background
      ? `/assets/avatar/backgrounds/${avatar.background}.svg`
      : '/assets/avatar/backgrounds/background-default.svg'

  const fallbackSrc = userAvatar || null

  const hasImage = characterSrc || fallbackSrc

  return (
    <div
      onClick={onClick}
      style={naked ? {
        width: size,
        height: size + 20,
        flexShrink: 0,
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform .15s',
      } : {
        width: size,
        height: size + 20,
        borderRadius: 24,
        background: '#E8F0FE',
        flexShrink: 0,
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform .15s',
        boxShadow: '0 8px 24px rgba(79,107,237,.15)',
      }}
      onMouseEnter={onClick ? (e) => (e.currentTarget.style.transform = 'scale(1.04)') : undefined}
      onMouseLeave={onClick ? (e) => (e.currentTarget.style.transform = 'scale(1)') : undefined}
    >
      <style>{`
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>

      {/* Layer 0: Background */}
      {bgSrc && (
        <div style={LAYER_STYLES.background}>
          <img
            src={bgSrc}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        </div>
      )}

      {/* Layer 1: Character body (with outfit baked in) */}
      <div style={{ ...LAYER_STYLES.wrapper, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
        {hasImage ? (
          <img
            src={characterSrc || fallbackSrc}
            alt="avatar"
            style={LAYER_STYLES.character}
            onError={(e) => {
              if (characterSrc && fallbackSrc && e.currentTarget.src !== fallbackSrc) {
                e.currentTarget.src = fallbackSrc
              } else {
                e.currentTarget.style.display = 'none'
                e.currentTarget.parentElement.querySelector('.emoji-fallback').style.display = 'flex'
              }
            }}
          />
        ) : null}
        <div
          className="emoji-fallback"
          style={{
            display: hasImage ? 'none' : 'flex',
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size * 0.4,
          }}
        >
          <img src={FALLBACK_IMG} alt="character" style={{ width: '60%', height: '60%', objectFit: 'contain' }} />
        </div>
      </div>

      {/* Layer 2: Hat overlay */}
      {hatSrc && (
        <img
          src={hatSrc}
          alt=""
          style={LAYER_STYLES.hat}
          onError={(e) => { e.currentTarget.style.display = 'none' }}
        />
      )}

      {/* Layer 2: Hand item overlay */}
      {handItemSrc && (
        <img
          src={handItemSrc}
          alt=""
          style={LAYER_STYLES.handItem}
          onError={(e) => { e.currentTarget.style.display = 'none' }}
        />
      )}

      {/* Sparkle effects */}
      {showSparkles && <Sparkles />}

      {/* Edit badge */}
      {onClick && (
        <div style={{
          position: 'absolute', bottom: 8, right: 8,
          width: 28, height: 28, borderRadius: '50%',
          background: '#fff', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: 14, boxShadow: '0 2px 8px rgba(0,0,0,.15)',
          zIndex: 4,
        }}>
          <img src="/assets/icons/pencil.png" alt="edit" style={{ width: 14, height: 14, objectFit: 'contain' }} />
        </div>
      )}
    </div>
  )
}

export default AvatarDisplay
